// src/prototype/components/EATabBar.tsx
// ============================================================
// Two-mode tab bar for the Evaluate page
// Tabs: Portfolio Strategy | Use Case Explorer
// Sits directly under the journey bar (inside sticky header)
// Microcopy replaced with tooltip icons (Phase 1 / Item 4)
// UX update March 2026: renamed tabs + artifact-level actions
// UX update March 2026: Adjust Weights added as contextual utility
//   action — visible on Use Case Explorer. Sits left of Export/Share
//   with a separator.
// ============================================================

import React, { useState } from 'react';
import { FileBarChart2, Search, Info, FileDown, Share2, SlidersHorizontal } from 'lucide-react';

export type EvaluateTab = 'report' | 'explore';

/** Keep old bookmark/state names resolving to the same tab. */
export function resolveEvaluateTab(tab: unknown): EvaluateTab {
  if (tab === 'explore') return 'explore';
  return 'report';
}

interface TabConfig {
  id: EvaluateTab;
  label: string;
  icon: React.ReactNode;
  tooltip: string;
}

const TABS: TabConfig[] = [
  {
    id: 'report',
    label: 'Portfolio Strategy',
    icon: <FileBarChart2 className="w-3.5 h-3.5" />,
    tooltip: 'Executive portfolio summary, strategic findings, and recommendations.',
  },
  {
    id: 'explore',
    label: 'Use Case Explorer',
    icon: <Search className="w-3.5 h-3.5" />,
    tooltip: 'Filter, compare ROI, flag assumptions, and select initiatives.',
  },
];

// ── Inline Tooltip ─────────────────────────────────────────────────────────
const TabTooltip: React.FC<{ text: string; isActive: boolean }> = ({ text, isActive }) => {
  const [visible, setVisible] = useState(false);

  return (
    <span
      className="relative flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <Info
        className={`w-3 h-3 transition-colors ${
          isActive ? 'text-white/60 hover:text-white' : 'text-[#18365E]/30 hover:text-[#18365E]/60'
        }`}
      />
      {visible && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-44 px-2.5 py-1.5 bg-[#18365E] text-white text-[10px] leading-snug rounded-lg shadow-lg pointer-events-none z-50 whitespace-normal">
          {text}
          {/* Arrow */}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#18365E]" />
        </span>
      )}
    </span>
  );
};

// ── Tab Bar ────────────────────────────────────────────────────────────────
interface EATabBarProps {
  activeTab: EvaluateTab;
  onChange: (tab: EvaluateTab) => void;
  /** Show "Adjust Weights" utility button — only when on explore tab + weights customized */
  showAdjustWeights?: boolean;
  /** Handler for Adjust Weights click */
  onAdjustWeights?: () => void;
}

export const EATabBar: React.FC<EATabBarProps> = ({
  activeTab,
  onChange,
  showAdjustWeights = false,
  onAdjustWeights,
}) => {
  const [shareLabel, setShareLabel] = useState('Share');

  const handleExport = () => {
    window.print();
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
      setShareLabel('Copied');
      window.setTimeout(() => setShareLabel('Share'), 1600);
    } catch {
      setShareLabel('Share');
    }
  };

  return (
    <div className="mt-1 mb-0 flex items-center justify-between gap-3">

      {/* Left: pill tabs */}
      <div className="flex items-center gap-1 p-1 bg-[#18365E]/[0.06] rounded-xl w-fit">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                isActive
                  ? 'bg-[#18365E] text-white shadow-sm'
                  : 'text-[#18365E]/70 hover:text-[#18365E] hover:bg-white/60'
              }`}
            >
              <span className={isActive ? 'text-white' : 'text-[#18365E]/50'}>
                {tab.icon}
              </span>
              {tab.label}
              <TabTooltip text={tab.tooltip} isActive={isActive} />
            </button>
          );
        })}
      </div>

      {/* Right: artifact-level actions */}
      <div className="flex items-center gap-2 shrink-0 print:hidden">
        {showAdjustWeights && onAdjustWeights && (
          <>
            <button
              type="button"
              onClick={onAdjustWeights}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Adjust Weights
            </button>
            <div className="w-px h-4 bg-[#e5e7eb]" />
          </>
        )}

        <button
          type="button"
          onClick={handleExport}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors"
        >
          <FileDown className="w-3.5 h-3.5" />
          Export
        </button>
        <button
          type="button"
          onClick={() => { void handleShare(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
          {shareLabel}
        </button>
      </div>

    </div>
  );
};

export default EATabBar;